import React from "react";
import ReactECharts from "echarts-for-react";
import ChartCard from "../ChartCard";
import { COLORS } from "../../mockData";

const AverageScoreTrend = ({ data, loading, onCardClick }) => {
  if (!data && !loading) return null;

  const handleChartClick = (params) => {
    if (!onCardClick || params.componentType !== "series") return;
    onCardClick({
      cardType: "averageScoreTrend",
      assessmentType: params.seriesName === "PHQ-9" ? "phq9" : "gad7",
      timeRange: data?.timeRange,
      viewType: "included",
      bucketIndex: params.dataIndex,
      sectionTitle: data?.title || "Average Score Trend",
      cardLabel: params.seriesName,
    });
  };

  const xLabels = data?.xAxis || [];
  const phq9Series = data?.yAxis?.find((s) => s.label === "PHQ-9");
  const gad7Series = data?.yAxis?.find((s) => s.label === "GAD-7");
  const phq9 = phq9Series?.data || [];
  const gad7 = gad7Series?.data || [];
  const phq9Counts = phq9Series?.patientCounts || [];
  const gad7Counts = gad7Series?.patientCounts || [];

  const needsRotation = xLabels.length > 6;
  const rotateAngle = needsRotation ? 35 : 0;

  const option = {
    tooltip: {
      trigger: "axis",
      formatter: (params) => {
        if (!params?.length) return "";
        let html = `<strong>${params[0].axisValue}</strong>`;
        params.forEach((p) => {
          const counts =
            p.seriesName === "PHQ-9" ? phq9Counts : gad7Counts;
          const count = counts[p.dataIndex] ?? "";
          const val = p.value != null ? p.value : "N/A";
          html += `<br/>${p.marker} ${p.seriesName}: ${val}`;
          if (count !== "") html += ` (${count} patients)`;
        });
        return html;
      },
    },
    legend: {
      data: ["PHQ-9", "GAD-7"],
      top: 0,
      right: 0,
      icon: "rect",
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { color: COLORS.body, fontSize: 12 },
    },
    grid: {
      left: 60,
      right: "4%",
      bottom: needsRotation ? 90 : 50,
      top: "12%",
      containLabel: false,
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: xLabels,
      axisLine: { lineStyle: { color: COLORS.border } },
      axisLabel: {
        color: COLORS.body,
        fontSize: 11,
        rotate: rotateAngle,
        overflow: "truncate",
      },
      name: data?.xAxisLabel || "Time buckets",
      nameLocation: "middle",
      nameGap: needsRotation ? 70 : 35,
      nameTextStyle: { color: COLORS.body, fontSize: 12 },
    },
    yAxis: {
      type: "value",
      axisLine: { show: true, lineStyle: { color: COLORS.border } },
      splitLine: { lineStyle: { color: COLORS.border, type: "dashed" } },
      axisLabel: { color: COLORS.body, fontSize: 11 },
      name: data?.yAxisLabel || "Average score",
      nameLocation: "middle",
      nameGap: 40,
      nameTextStyle: { color: COLORS.body, fontSize: 12 },
    },
    series: [
      {
        name: "PHQ-9",
        type: "line",
        smooth: false,
        connectNulls: false,
        symbol: "circle",
        symbolSize: 7,
        lineStyle: { color: COLORS.phq9, width: 2 },
        itemStyle: { color: COLORS.phq9, cursor: onCardClick ? "pointer" : "default" },
        areaStyle: {
          color: {
            type: "linear",
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: "rgba(132, 94, 244, 0.35)" },
              { offset: 1, color: "rgba(132, 94, 244, 0.05)" },
            ],
          },
        },
        data: phq9,
      },
      {
        name: "GAD-7",
        type: "line",
        smooth: false,
        connectNulls: false,
        symbol: "circle",
        symbolSize: 7,
        lineStyle: { color: COLORS.gad7, width: 2 },
        itemStyle: { color: COLORS.gad7, cursor: onCardClick ? "pointer" : "default" },
        areaStyle: {
          color: {
            type: "linear",
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: "rgba(11, 146, 254, 0.35)" },
              { offset: 1, color: "rgba(11, 146, 254, 0.05)" },
            ],
          },
        },
        data: gad7,
      },
    ],
  };

  const minWidth = xLabels.length > 6 ? xLabels.length * 80 : undefined;
  const chartHeight = needsRotation ? 400 : 320;

  return (
    <ChartCard
      title={data?.title || "Average Score Trend"}
      subtitle={data?.timeRange || ""}
      minWidth={minWidth}
    >
      <ReactECharts
        option={option}
        style={{ height: chartHeight, minWidth: minWidth || "100%" }}
        onEvents={onCardClick ? { click: handleChartClick } : undefined}
      />
    </ChartCard>
  );
};

export default AverageScoreTrend;
